import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Plus, Send } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { usePageActions } from "@/shared/hooks/usePageActions";
import { ApiError } from "@/shared/lib/apiClient";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { StatusBadge } from "../components/StatusBadge";
import { FormField, selectClass } from "../components/FormField";
import { publishAdminCompanyBilling } from "../api/adminApi";
import { useAdminCompanyBilling } from "../hooks/useAdminCompanyBilling";

const statusOptions = [
  { value: "", label: "Tous les statuts" },
  { value: "draft", label: "Brouillon" },
  { value: "published", label: "Publié" },
  { value: "paid", label: "Payé" },
  { value: "cancelled", label: "Annulé" },
];

function formatUsd(amount: number): string {
  return `${amount.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`;
}

export function CompanyBillingPage() {
  const { t } = useTranslation();
  const { showToast } = usePageActions();
  const [statusFilter, setStatusFilter] = useState("");
  const { statements, loading, error, reload } = useAdminCompanyBilling(
    undefined,
    statusFilter,
  );
  const [publishingId, setPublishingId] = useState<number | null>(null);

  const rows = useMemo(() => statements, [statements]);

  const handlePublish = async (id: number) => {
    setPublishingId(id);
    try {
      await publishAdminCompanyBilling(id);
      showToast({ message: "Relevé publié — visible par l'entreprise.", variant: "success" });
      await reload();
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Publication impossible.";
      showToast({ message, variant: "warning" });
    } finally {
      setPublishingId(null);
    }
  };

  return (
    <>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <Breadcrumbs
          homeHref="/dashboard/admin"
          items={[{ label: t.safecheck.nav.companyBilling }]}
        />
        <Link
          to="/dashboard/company-billing/create"
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
        >
          <Plus size={15} />
          {t.safecheck.nav.createCompanyBilling}
        </Link>
      </div>

      {error && <p className="mb-3 text-sm text-danger">{error}</p>}

      <div className="theme-card p-4">
        <div className="mb-4 max-w-xs">
          <FormField label="Statut">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={selectClass}
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </FormField>
        </div>

        {loading ? (
          <p className="py-8 text-center text-sm text-muted">Chargement…</p>
        ) : rows.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted">Aucun relevé entreprise.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="px-3 py-2 font-medium">#</th>
                  <th className="px-3 py-2 font-medium">Entreprise</th>
                  <th className="px-3 py-2 font-medium">Libellé</th>
                  <th className="px-3 py-2 font-medium">Montant</th>
                  <th className="px-3 py-2 font-medium">Statut</th>
                  <th className="px-3 py-2 font-medium">Période</th>
                  <th className="px-3 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-b border-border/60 hover:bg-card-muted/40">
                    <td className="px-3 py-3">{row.id}</td>
                    <td className="px-3 py-3">{row.company_name}</td>
                    <td className="px-3 py-3">{row.label}</td>
                    <td className="px-3 py-3 font-medium">{formatUsd(row.montant_usd)}</td>
                    <td className="px-3 py-3">
                      <StatusBadge
                        variant={
                          row.status === "published" || row.status === "paid"
                            ? "success"
                            : row.status === "draft"
                              ? "warning"
                              : "neutral"
                        }
                      >
                        {row.status}
                      </StatusBadge>
                    </td>
                    <td className="px-3 py-3 text-muted">
                      {row.periode_debut || "—"}
                      {row.periode_fin ? ` → ${row.periode_fin}` : ""}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex justify-end gap-2">
                        <Link
                          to={`/dashboard/company-billing/${row.id}/edit`}
                          className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-xs hover:bg-card-muted"
                        >
                          <Eye size={13} />
                          Voir
                        </Link>
                        {row.status === "draft" && (
                          <button
                            type="button"
                            disabled={publishingId === row.id}
                            onClick={() => void handlePublish(row.id)}
                            className="inline-flex items-center gap-1 rounded-lg bg-teal px-2.5 py-1.5 text-xs font-medium text-white hover:bg-teal/90 disabled:opacity-60"
                          >
                            <Send size={13} />
                            {publishingId === row.id ? "…" : "Publier"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
